/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ComponentData, ComponentModel, ComponentFieldKey } from '../model/component.model';
import { ObservableList, forEach, ObserveModes } from '@typescript-kit/core';
import { ViewComponentKey } from '../key/view-component.key';
import { TableRowData, TableRowModel } from './table-row.component-model';
import { TableColumnData, TableColumnModel } from './table-column.component-model';
import { TableCellData, TableCellModel } from './table-cell.component-model';

export interface TableData extends ComponentData {
  rowCount?: number;
  columnCount?: number;
  rows?: Iterable<TableRowData>;
  columns?: Iterable<TableColumnData>;
  cells?: Iterable<TableCellData>;
  columnHeaderCount?: number;
  rowHeaderCount?: number;
}

export class TableFieldKey {
  static readonly ROW_COUNT = 'rowCount';
  static readonly COLUMN_COUNT = 'columnCount';
  static readonly ROWS = 'rows';
  static readonly COLUMNS = 'columns';
  static readonly CELLS = 'cells';
  static readonly COLUMN_HEADER_COUNT = 'columnHeaderCount';
  static readonly ROW_HEADER_COUNT = 'rowHeaderCount';
}

export class TableModel extends ComponentModel implements TableData {
  static readonly KEYS = ComponentModel.KEYS.concat(
    Object.keys(TableFieldKey).map((key) => TableFieldKey[key])
  );

  private _rowArray: TableRowModel[];
  private _columnArray: TableColumnModel[];
  private _cellArray: TableCellModel[][];

  constructor(data?: TableData, keys?: string[]) {
    super(data, keys || TableModel.KEYS, { observeMode: ObserveModes.FLAT });
  }

  get rowCount(): number {
    return this.get(TableFieldKey.ROW_COUNT);
  }

  set rowCount(value: number) {
    this.set(TableFieldKey.ROW_COUNT, value);
  }

  get columnCount(): number {
    return this.get(TableFieldKey.COLUMN_COUNT);
  }

  set columnCount(value: number) {
    this.set(TableFieldKey.COLUMN_COUNT, value);
  }

  get rows(): ObservableList<TableRowModel> {
    return this.get(TableFieldKey.ROWS);
  }

  set rows(value: ObservableList<TableRowModel>) {
    this.set(TableFieldKey.ROWS, value);
  }

  get rowArray(): TableRowModel[] {
    return this._rowArray;
  }

  getRow(index: number) {
    return index < this.rowArray.length ? this.rowArray[index] : null;
  }

  get columns(): ObservableList<TableColumnModel> {
    return this.get(TableFieldKey.COLUMNS);
  }

  set columns(value: ObservableList<TableColumnModel>) {
    this.set(TableFieldKey.COLUMNS, value);
  }

  get columnArray(): TableColumnModel[] {
    return this._columnArray;
  }

  getColumn(index: number) {
    return index < this.columnArray.length ? this.columnArray[index] : null;
  }

  get cells(): ObservableList<TableCellModel> {
    return this.get(TableFieldKey.CELLS);
  }

  set cells(value: ObservableList<TableCellModel>) {
    this.set(TableFieldKey.CELLS, value);
  }

  get cellArray(): TableCellModel[][] {
    return this._cellArray;
  }

  getCell(row: number, column: number) {
    return row < this.cellArray.length && column < this.cellArray[row].length ? this._cellArray[row][column] : null;
  }

  get columnHeaderCount(): number {
    return this.get(TableFieldKey.COLUMN_HEADER_COUNT);
  }

  set columnHeaderCount(value: number) {
    this.set(TableFieldKey.COLUMN_HEADER_COUNT, value);
  }

  get rowHeaderCount(): number {
    return this.get(TableFieldKey.ROW_HEADER_COUNT);
  }

  set rowHeaderCount(value: number) {
    this.set(TableFieldKey.ROW_HEADER_COUNT, value);
  }

  protected createChildrenIterable(): Iterable<ComponentModel> {
    const self = this;
    return {
      [Symbol.iterator]: function* createChildrenGenerator() {
        for (const row of self.rows) {
          yield row;
        }
        for (const column of self.columns) {
          yield column;
        }
        for (const cell of self.cells) {
          yield cell;
        }
      }
    };
  }

  protected setupValues(values: TableData, exclusive: boolean): TableData {
    // TODO: Listen on observable list changed: update columns, rows, cells
    // TODO: Reset child parents on model changes
    if (exclusive && !(ComponentFieldKey.TYPE in values)) {
      values.type = ViewComponentKey.TABLE;
    }
    this.setupColumns(values, exclusive);
    this.setupRows(values, exclusive);
    this.setupCells(values, exclusive);
    // append missing column models
    if (this.cellArray.length > 0 && this.columnArray.length < this.cellArray[0].length) {
      for (let index = this.columnArray.length; index < this.cellArray[0].length; index++) {
        this.columnArray[index] = new TableColumnModel({ index });
        this.setChildParent(this.columnArray[index]);
      }
    }
    // append missing row models
    if (this.rowArray.length < this.cellArray.length) {
      for (let index = this.rowArray.length; index < this.cellArray.length; index++) {
        this.rowArray[index] = new TableRowModel({ index });
        this.setChildParent(this.rowArray[index]);
      }
    }

    if (exclusive || TableFieldKey.COLUMNS in values) {
      if (values.columns instanceof ObservableList) {
        values.columns.setValues(this.columnArray, { exclusive: true });
      } else {
        values.columns = new ObservableList(this.columnArray, { observeMode: ObserveModes.FLAT });
      }
    } else if (this.columnArray.length !== this.columns.size) {
      this.columns.setValues(this.columnArray, { exclusive: true });
    }

    if (exclusive || TableFieldKey.ROWS in values) {
      if (values.rows instanceof ObservableList) {
        values.rows.setValues(this.rowArray, { exclusive: true });
      } else {
        values.rows = new ObservableList(this.rowArray, { observeMode: ObserveModes.FLAT });
      }
    } else if (this.rowArray.length !== this.rows.size) {
      this.rows.setValues(this.rowArray, { exclusive: true });
    }

    if (exclusive || TableFieldKey.CELLS in values) {
      const cellArray = this.cellArray.reduce((totalCells, rowCells) => totalCells.concat(rowCells), []);
      if (values.cells instanceof ObservableList) {
        values.cells.setValues(cellArray, { exclusive: true });
      } else {
        values.cells = new ObservableList(cellArray, { observeMode: ObserveModes.FLAT });
      }
    } else {
      const cellCount = this.cellArray.length > 0 ? this.cellArray.length * this.cellArray[0].length : 0;
      if (cellCount !== this.cells.size) {
        const cellArray = this.cellArray.reduce((totalCells, rowCells) => totalCells.concat(rowCells), []);
        this.cells.setValues(cellArray, { exclusive: true });
      }
    }

    values.rowCount = this.rowArray.length;
    values.columnCount = this.columnArray.length;

    if (exclusive && !(TableFieldKey.COLUMN_HEADER_COUNT in values)) {
      values.columnHeaderCount = 0;
    }
    if (exclusive && !(TableFieldKey.ROW_HEADER_COUNT in values)) {
      values.rowHeaderCount = 0;
    }

    return super.setupValues(values, exclusive) as TableData;
  }

  protected setupColumns(tableData: TableData, exclusive: boolean): TableColumnModel[] {
    if (!exclusive && !(TableFieldKey.COLUMNS in tableData) && !(TableFieldKey.COLUMN_COUNT in tableData)) {
      return;
    }
    // if (tableData.columns instanceof ObservableList) {
    //   this._columnArray = tableData.columns.toArray();
    // }
    const columnCount = tableData.columnCount || 0;
    const columnDataList = this.getSetupValue(tableData, TableFieldKey.COLUMNS, exclusive);
    this._columnArray = this.createDimensionModelList(
      columnCount, columnDataList, (columnData: TableColumnData) => new TableColumnModel(columnData)
    );
  }

  protected setupRows(tableData: TableData, exclusive: boolean) {
    if (!exclusive && !(TableFieldKey.ROWS in tableData) && !(TableFieldKey.ROW_COUNT in tableData)) {
      return;
    }
    // if (tableData.rows instanceof ObservableList) {
    //   this._rowArray = tableData.rows.toArray();
    // }
    const rowCount = tableData.rowCount || 0;
    const rowDataList = this.getSetupValue(tableData, TableFieldKey.ROWS, exclusive);
    this._rowArray = this.createDimensionModelList(
      rowCount, rowDataList, (rowData: TableRowData) => new TableRowModel(rowData)
    );
  }

  protected setupCells(tableData: TableData, exclusive: boolean) {
    if (!exclusive &&
      !(TableFieldKey.CELLS in tableData) &&
      !(TableFieldKey.ROW_COUNT in tableData) &&
      !(TableFieldKey.COLUMN_COUNT in tableData)
    ) {
      return;
    }
    // if (tableData.cells instanceof ObservableList) {
    //   this._cellArray = create 2-dimensional array
    // }
    const rowCount = tableData.rowCount || 0;
    const columnCount = tableData.columnCount || 0;
    const cellDataList = this.getSetupValue(tableData, TableFieldKey.CELLS, exclusive);
    this._cellArray = this.createCellModelList(rowCount, columnCount, cellDataList);
  }

  protected createDimensionModelList<TData extends TableRowData | TableColumnData, TModel>(
    count: number, dataList: Iterable<TData>, modelFactory: (data: TData) => TModel
  ): TModel[] {
    const modelList: TModel[] = new Array<TModel>(count);
    let index: number = 0;
    if (dataList) {
      const unindexedItems: TableRowData | TableColumnData[] = [];
      // indexed items
      forEach(dataList, (dataItem: TData) => {
        if (typeof dataItem.index === 'number') {
          if (dataItem.index < 0) {
            throw new Error('NOT IMPLEMENTED: invalid index'); // invalid index
          } else if (count && dataItem.index >= count) {
            throw new Error('NOT IMPLEMENTED: out of range'); // out of range
          } else if (dataItem.index < modelList.length && modelList[dataItem.index]) {
            throw new Error('NOT IMPLEMENTED: duplicate index'); // duplicate
          }
          modelList[dataItem.index] = dataItem instanceof ComponentModel ? dataItem as any : modelFactory(dataItem);
          this.setChildParent(modelList[dataItem.index]);
        } else if (!dataItem.index) {
          unindexedItems.push(dataItem);
        } else {
          throw new Error('NOT IMPLEMENTED'); // invalid index
        }
      });
      // unindexed items
      forEach(unindexedItems, (dataItem: TData) => {
        while (index < modelList.length && modelList[index]) {
          index++;
        }
        if (count && index >= count) {
          throw new Error('NOT IMPLEMENTED: to many row items');
        } else {
          dataItem.index = index;
          modelList[index] = dataItem instanceof ComponentModel ? dataItem as any : modelFactory(dataItem);
          this.setChildParent(modelList[index]);
          index++;
        }
      });
    }
    // remaining items
    while (index < modelList.length) {
      if (!modelList[index]) {
        modelList[index] = modelFactory({ index } as TData);
        this.setChildParent(modelList[index]);
      }
      index++;
    }
    return modelList;
  }

  private createCellModelList(
    rowCount: number, columnCount: number, dataList: Iterable<TableCellData>
  ): TableCellModel[][] {
    let actualColumnCount: number = columnCount || 0;
    const cellModelList: TableCellModel[][] = new Array<TableCellModel[]>(rowCount);
    if (dataList) {
      forEach(dataList, (dataItem: TableCellData) => {
        if (typeof dataItem.row !== 'number' || dataItem.row < 0) {
          throw new Error('NOT IMPLEMENTED: invalid row'); // invalid row
        } else if (typeof dataItem.column !== 'number' || dataItem.column < 0) {
          throw new Error('NOT IMPLEMENTED: invalid column'); // invalid column
        } else if (rowCount && dataItem.row >= rowCount || columnCount && dataItem.column >= columnCount) {
          throw new Error('NOT IMPLEMENTED: out of range'); // out of range
        } else {
          if (cellModelList.length <= dataItem.row || !cellModelList[dataItem.row]) {
            cellModelList[dataItem.row] = new Array<TableCellModel>(columnCount);
          }
          const row: TableCellModel[] = cellModelList[dataItem.row];
          if (dataItem.column < row.length && row[dataItem.column]) {
            throw new Error('NOT IMPLEMENTED: duplicate row column pair'); // duplicate
          } else {
            if (dataItem.column >= actualColumnCount) {
              actualColumnCount = dataItem.column + 1;
            }
            row[dataItem.column] = dataItem instanceof ComponentModel ? dataItem as any : new TableCellModel(dataItem);
            this.setChildParent(row[dataItem.column]);
            if (dataItem.rowSpan > 1) {
              for (let i = 1; i < dataItem.rowSpan; i++) {
                if (!cellModelList[dataItem.row + i]) {
                  cellModelList[dataItem.row + i] = new Array<TableCellModel>(columnCount);
                }
                // console.log(dataItem.row + i, dataItem.column, 'NULL');
                cellModelList[dataItem.row + i][dataItem.column] = null;
              }
            }
          }
        }
      });
    }
    // remaining items
    if (!rowCount) {
      rowCount = cellModelList.length;
    }
    if (!columnCount) {
      columnCount = actualColumnCount;
    }
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
      if (!cellModelList[rowIndex]) {
        cellModelList[rowIndex] = new Array(columnCount);
      }
      for (let columnIndex = 0; columnIndex < columnCount; columnIndex++) {
        if (cellModelList[rowIndex].length <= columnIndex || cellModelList[rowIndex][columnIndex] === undefined) {
          cellModelList[rowIndex][columnIndex] = new TableCellModel({ row: rowIndex, column: columnIndex });
          this.setChildParent(cellModelList[rowIndex][columnIndex]);
        }
      }
    }
    return cellModelList;
  }

}
